import { useEffect, useState } from 'react';
import { Banner, BigButton, Card, Choice, Label, Screen, Toggle } from '../components/ui';
import { bytes, fullDateTime } from '../lib/format';
import { PHOTO_PROFILES } from '../lib/image';
import {
  hasBackgroundSync,
  requestPersistentStorage,
  storageEstimate,
} from '../lib/service-worker';
import { flushNow, useSyncState } from '../lib/sync';
import { useSession } from '../state/session';
import { useSettings } from '../state/settings';

/** Настройки. Всичко тук е обяснено с думи — включително защо нещо е изключено. */
export function SettingsScreen() {
  const settings = useSettings();
  const sync = useSyncState();
  const { driver, signOut, lock } = useSession();
  const [storage, setStorage] = useState<{ usedBytes: number; quotaBytes: number } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void storageEstimate().then(setStorage);
  }, [sync.pending]);

  const profile = PHOTO_PROFILES[settings.photoQuality];
  // Типична доставка: ЧМР в две страници, разписка, подпис и по някоя снимка на товара.
  const perDeliveryKb = profile.approxKb * 4 + 60;

  return (
    <Screen title="Настройки" back>
      <div className="space-y-4">
        {driver ? (
          <Card>
            <Label>Шофьор</Label>
            <p className="text-2xl font-semibold">{driver.fullName}</p>
          </Card>
        ) : null}

        <div>
          <Label>Език</Label>
          <p className="mt-1 text-2xl font-semibold">Български</p>
          <p className="text-base text-muted">
            Приложението е само на български. Друг език ще бъде добавен, когато във фирмата
            постъпи шофьор, който не чете български.
          </p>
        </div>

        <div>
          <Label>Изглед</Label>
          <div className="mt-1 space-y-2">
            <Choice
              value={settings.theme}
              onChange={settings.setTheme}
              options={[
                { value: 'day', label: 'Дневен' },
                { value: 'night', label: 'Нощен' },
              ]}
            />
            <Choice
              value={settings.textScale}
              onChange={settings.setTextScale}
              options={[
                { value: 'normal', label: 'Нормален текст' },
                { value: 'large', label: 'Едър текст' },
              ]}
            />
          </div>
        </div>

        <div>
          <Label>Снимки и мобилни данни</Label>
          <div className="mt-1 space-y-2">
            <Choice
              value={settings.photoQuality}
              onChange={settings.setPhotoQuality}
              options={[
                { value: 'normal', label: 'Нормални' },
                { value: 'economy', label: 'Пестеливи' },
              ]}
            />
            <Card>
              <p className="text-lg">
                Снимката се смалява на телефона до {profile.maxEdge} пиксела по дългата страна и се
                записва като JPEG с качество {profile.quality.toFixed(2)} — около {profile.approxKb}{' '}
                KB на страница.
              </p>
              <p className="mt-2 text-lg font-semibold">
                Една доставка изразходва около {(perDeliveryKb / 1024).toFixed(1)} MB — четири
                страници документи плюс данните за курса. При около 52 курса на месец това е под
                100 MB, включително в роуминг.
              </p>
            </Card>
            <Toggle
              label="Качвай снимките и по мобилни данни"
              description="Изключено: снимките чакат Wi-Fi. Събитията и съобщенията тръгват винаги."
              checked={settings.uploadPhotosOnMobileData}
              onChange={settings.setUploadPhotosOnMobileData}
            />
          </div>
        </div>

        <div>
          <Label>Местоположение</Label>
          <Toggle
            label="Позиция и извън работните действия"
            description="Изключено по подразбиране."
            checked={settings.backgroundPosition}
            onChange={settings.setBackgroundPosition}
          />
          <Card className="mt-2">
            <p className="text-lg">
              Позицията на буса се следи от бордовото устройство на автомобила — то е официалният
              източник. Телефонът дава координати само в пет момента: пристигане за товарене,
              натоварване, тръгване, пристигане за разтоварване и разтоварване.
            </p>
            <p className="mt-2 text-lg">
              Затова тази настройка стои изключена: извън тези моменти вие не сте проследяван
              лично. Включете я само ако диспечерът изрично ви е помолил и ви е обяснил защо.
            </p>
          </Card>
        </div>

        <Toggle
          label="Екранът да не изгасва"
          description="Полезно, когато телефонът е в стойката на таблото."
          checked={settings.keepScreenAwake}
          onChange={settings.setKeepScreenAwake}
        />

        <div>
          <Label>Синхронизация</Label>
          <Card className="mt-1">
            <p className="text-lg">
              Състояние: {sync.online ? 'има връзка' : 'няма връзка'}
              {sync.running ? ' · изпраща в момента' : ''}
            </p>
            <p className="text-lg">Чакащи записи: {sync.pending}</p>
            {sync.blocked > 0 ? (
              <p className="text-lg font-semibold text-stop">
                Записи с грешка: {sync.blocked} — покажете ги на диспечера.
              </p>
            ) : null}
            <p className="text-lg">
              Последно изпращане: {sync.lastSyncAt ? fullDateTime(sync.lastSyncAt) : '—'}
            </p>
            <p className="text-base text-muted">
              Фонова синхронизация: {hasBackgroundSync() ? 'налична' : 'не се поддържа от този телефон'}.
            </p>
          </Card>
          <div className="mt-2">
            <BigButton
              tone="brand"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void flushNow().finally(() => setBusy(false));
              }}
            >
              {busy ? 'Изпраща…' : 'Синхронизирай сега'}
            </BigButton>
          </div>
        </div>

        <div>
          <Label>Памет на телефона</Label>
          <Card className="mt-1">
            <p className="text-lg">
              Заето от приложението: {storage ? bytes(storage.usedBytes) : '—'}
              {storage && storage.quotaBytes > 0 ? ` от ${bytes(storage.quotaBytes)}` : ''}
            </p>
            <button
              type="button"
              onClick={() => void requestPersistentStorage()}
              className="tap mt-2 w-full rounded-xl border-2 border-line px-4 py-3 text-lg font-semibold"
            >
              Защити данните от изтриване
            </button>
          </Card>
        </div>

        <p className="text-base text-muted">Версия от {fullDateTime(__BUILD_DATE__)}</p>

        <BigButton tone="plain" onClick={lock}>
          Заключи екрана
        </BigButton>

        {sync.pending > 0 ? (
          <Banner tone="warn">
            Има {sync.pending} неизпратени записа. Изчакайте връзка и ги изпратете, преди да
            излезете — изходът изтрива всичко от телефона.
          </Banner>
        ) : null}

        <BigButton
          tone="stop"
          onClick={() => {
            if (
              window.confirm(
                'Изходът изтрива всички данни от телефона и ще трябва нов код от диспечера. Сигурни ли сте?',
              )
            ) {
              void signOut();
            }
          }}
        >
          Изход от приложението
        </BigButton>
      </div>
    </Screen>
  );
}
